import React from 'react';
import type { AuditEventFacets } from '@/types';
import {
  CATEGORY_ORDER,
  categoryMeta,
  actorFacetName,
  actorFacetShortName,
  formatUsd,
  statusBarBg,
  statusLabel,
} from './auditMeta';
import { buildTopPeople } from './auditState';

interface AuditDistributionBarProps {
  facets?: AuditEventFacets;
  /** True while the first facets request is in flight (no data yet). */
  loading?: boolean;
  /** Status view: left block = Statuses (from byStatus) instead of Categories. */
  statusView: boolean;
  activeCategories: string[];
  activeStatuses: string[];
  /** The currently-filtered actor key, or null. */
  activeActor: string | null;
  onToggleCategory: (category: string) => void;
  onToggleStatus: (status: string) => void;
  onToggleActor: (actorKey: string) => void;
}

interface Segment {
  key: string;
  label: string;
  /** Drives the segment width (relative to the block's own sum) and the legend number. */
  value: number;
  color: string; // tailwind bg-* class for the bar segment + legend dot
  active: boolean;
  /** Hover text, e.g. "181 deposits · 210 changes · $52,340.12". */
  tooltip: string;
  /** Absent = not clickable (e.g. the "Others" aggregate). */
  onClick?: () => void;
}

/** Distinct colors for the top people (legend dots match); System stays gray, Others lighter gray. */
const PEOPLE_COLORS = [
  'bg-blue-500',
  'bg-sky-400',
  'bg-indigo-400',
  'bg-cyan-500',
  'bg-violet-400',
  'bg-teal-400',
  'bg-blue-300',
  'bg-fuchsia-400',
  'bg-emerald-400',
];

const MASK = 'linear-gradient(black 88%, transparent 100%)';

function n(v: number): string {
  return (v ?? 0).toLocaleString();
}

/** One compact stat: label + thin segmented bar + masked, wrapping, clickable legend. */
const StatBlock: React.FC<{ title: string; segments: Segment[]; loading?: boolean }> = ({ title, segments, loading }) => {
  const present = segments.filter((s) => s.value > 0 || s.active);
  // Widths are relative to this block's own sum, so the bar never exceeds 100%.
  const sum = present.reduce((acc, s) => acc + Math.max(0, s.value), 0);
  const denom = sum > 0 ? sum : 1;
  return (
    <div className="flex min-w-0 flex-col gap-0">
      <span className="text-[9px] font-medium uppercase leading-tight tracking-wider text-gray-400">{title}</span>
      <div className="flex h-2 w-full overflow-hidden rounded-sm bg-gray-100">
        {present.map((s) => {
          const style = { width: `${(Math.max(0, s.value) / denom) * 100}%` };
          return s.onClick ? (
            <button
              key={s.key}
              type="button"
              title={s.tooltip}
              aria-label={`${s.label}: ${s.tooltip}. ${s.active ? 'Remove filter' : 'Filter'}`}
              aria-pressed={s.active}
              onClick={s.onClick}
              style={style}
              className={`${s.color} h-full cursor-pointer transition-all hover:opacity-80 ${
                s.active ? 'ring-2 ring-inset ring-gray-700' : ''
              }`}
            />
          ) : (
            <span key={s.key} title={s.tooltip} style={style} className={`${s.color} h-full`} />
          );
        })}
      </div>
      <div className="relative h-[1.4rem] overflow-hidden" style={{ maskImage: MASK, WebkitMaskImage: MASK }}>
        <div className="flex flex-wrap gap-x-3 gap-y-0 text-[10px] leading-[1.2] text-gray-500">
          {present.length === 0 ? (
            <span className="text-gray-300">{loading ? 'Loading…' : 'No data'}</span>
          ) : (
            present.map((s) =>
              s.onClick ? (
                <button
                  key={s.key}
                  type="button"
                  onClick={s.onClick}
                  title={`${s.tooltip} — ${s.active ? 'click to remove filter' : 'click to filter'}`}
                  aria-pressed={s.active}
                  className={`flex flex-shrink-0 items-center gap-1 transition ${
                    s.active ? 'font-semibold text-gray-900' : 'hover:text-gray-800'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${s.color}`} />
                  {s.label}: <strong>{n(s.value)}</strong>
                  {s.active && <i className="fas fa-check text-[8px]" aria-hidden="true" />}
                </button>
              ) : (
                <span key={s.key} title={s.tooltip} className="flex flex-shrink-0 items-center gap-1 text-gray-400">
                  <span className={`h-1.5 w-1.5 rounded-full ${s.color}`} />
                  {s.label}: <strong>{n(s.value)}</strong>
                </span>
              )
            )
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Two compact, clickable distribution dimensions in the deposits-page stat style.
 * LEFT: Statuses (status view — deposit.status_changed by new status) or Categories.
 * RIGHT: Top people (top 8 by count + an "Others" aggregate so the bar sums to the block total).
 * Bar segments and legend entries toggle the corresponding server-side filter.
 */
const AuditDistributionBar: React.FC<AuditDistributionBarProps> = ({
  facets,
  loading,
  statusView,
  activeCategories,
  activeStatuses,
  activeActor,
  onToggleCategory,
  onToggleStatus,
  onToggleActor,
}) => {
  let leftTitle: string;
  let leftSegments: Segment[];

  if (statusView) {
    leftTitle = 'Statuses';
    const byStatus = facets?.byStatus ?? [];
    // Legend number = CHANGES per status (matches the Top-people legend); deposits and dollars
    // are in the tooltip.
    leftSegments = byStatus.map((s) => ({
      key: `status:${s.status}`,
      label: statusLabel(s.status),
      value: s.count ?? 0,
      color: statusBarBg(s.status),
      active: activeStatuses.includes(s.status),
      tooltip: `${n(s.count)} changes · ${n(s.deposits)} deposits · ${formatUsd(s.amount)}`,
      onClick: () => onToggleStatus(s.status),
    }));
    // A selected status with no rows in range still shows (highlighted) so it can be removed.
    for (const st of activeStatuses) {
      if (!byStatus.some((s) => s.status === st)) {
        leftSegments.push({
          key: `status:${st}`,
          label: statusLabel(st),
          value: 0,
          color: statusBarBg(st),
          active: true,
          tooltip: '0 changes · 0 deposits',
          onClick: () => onToggleStatus(st),
        });
      }
    }
  } else {
    leftTitle = 'Categories';
    const byCategory = facets?.byCategory ?? {};
    const extra = Object.keys(byCategory).filter((c) => !(CATEGORY_ORDER as readonly string[]).includes(c));
    const cats = [...CATEGORY_ORDER, ...extra, ...activeCategories.filter((c) => !(c in byCategory) && !(CATEGORY_ORDER as readonly string[]).includes(c))];
    leftSegments = cats.map((cat) => ({
      key: `cat:${cat}`,
      label: categoryMeta(cat).label,
      value: byCategory[cat] || 0,
      color: categoryMeta(cat).barBg,
      active: activeCategories.includes(cat),
      tooltip: `${n(byCategory[cat] || 0)} events`,
      onClick: () => onToggleCategory(cat),
    }));
  }

  const people = buildTopPeople(facets?.byActor, activeActor, 8);
  const peopleSegments: Segment[] = people.top.map((a, i) => ({
    key: `actor:${a.key}`,
    label: actorFacetShortName(a),
    value: a.count,
    color: a.key === 'system' || a.key === 'anon' ? 'bg-gray-400' : PEOPLE_COLORS[i % PEOPLE_COLORS.length],
    active: activeActor === a.key,
    tooltip: statusView
      ? `${actorFacetName(a)}: ${n(a.count)} changes · ${n(a.deposits)} deposits · ${formatUsd(a.amount)}`
      : `${actorFacetName(a)}: ${n(a.count)} events`,
    onClick: () => onToggleActor(a.key),
  }));
  if (people.others && people.others.count > 0) {
    peopleSegments.push({
      key: 'actor:__others',
      label: 'Others',
      value: people.others.count,
      color: 'bg-gray-300',
      active: false,
      tooltip: `${n(people.others.people)} other people: ${n(people.others.count)} ${statusView ? 'changes' : 'events'}`,
    });
  }

  return (
    <div className="grid grid-cols-2 gap-3 pb-0.5">
      <StatBlock title={leftTitle} segments={leftSegments} loading={loading} />
      <StatBlock title="Top people" segments={peopleSegments} loading={loading} />
    </div>
  );
};

export default AuditDistributionBar;
